import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmationPopup from "../ConfirmationPopup";
import { doRequest, submitUserPlans } from "../../utils/helper";
import { config } from "../../utils/config";
import {
  setUserData,
} from "../../redux/slices/UserSlice";
import {
    getPlanDetails,
  setPlanDetails,
  setVariationCountDetails,
} from "../../redux/slices/PlanDetailSlice";
import { isEmptyObject } from "jquery";

export default function SubscribePlanButton({ plan }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData } = useSelector((state) => state?.user || {});
  const { userPlanDetails } = useSelector((state) => state?.userPlan || {});
  const userCurrentPlan = userPlanDetails?.[0] || {};

  const [showCancelPopup, setShowCancelPopup] = useState(false);

  const planId = +plan?.id;
  const planType = plan?.type;
  const currentPlanId = +userCurrentPlan?.plan_id || 0;

  /** ───────────────────────────────
   * Derived Values using useMemo
   ─────────────────────────────── */
  const currentPlanStatus = useMemo(() => {
    if ([1, 5].includes(currentPlanId)) {
      return userCurrentPlan.plan_status;
    }
    return userCurrentPlan.subscription_status;
  }, [userCurrentPlan, currentPlanId]);

  const isCurrentPlan = useMemo(
    () => currentPlanId === planId,
    [currentPlanId, planId]
  );

  const notEligible = useMemo(
    () => currentPlanId > 1 && planId < currentPlanId,
    [currentPlanId, planId]
  );

  const planExpired = useMemo(() => {
    if (currentPlanId === 5) return false; // prepaid plan never expires
    const endDate = new Date(userCurrentPlan?.end_date);
    return new Date() > endDate;
  }, [userCurrentPlan, currentPlanId]);

  /** ───────────────────────────────
   * Button Text
   ─────────────────────────────── */
  const buttonText = useMemo(() => {
    if (notEligible) return "Not Eligible";
    if (planExpired) return "Upgrade";
    if (isCurrentPlan && currentPlanStatus === "active") return "Current Plan";

    switch (planType) {
      case "Free":
        return "Get Free";
      case "Prepaid":
        return "Get Prepaid";
      case "Unlimited":
        return "Get Unlimited";
      default:
        return "Subscribe";
    }
  }, [notEligible, planExpired, isCurrentPlan, currentPlanStatus, planType]);

  const btnClass = "subbtn_bg"; // unified button class

  /** ───────────────────────────────
   * API Calls
   ─────────────────────────────── */
  const submitUserPlan = async () => {
    try {
      const userLoginDetails = JSON.parse(
        localStorage.getItem(config.USER_LOGIN_DETAILS)
      );

      const response = await submitUserPlans(planId, userLoginDetails);
      if (response?.resultFlag !== 1) return;

      const { user_data, userPlan, variants } = response?.response || {};
      dispatch(setUserData({ userData: user_data }));
      dispatch(setPlanDetails({ userPlanDetails: userPlan }));
      dispatch(setVariationCountDetails(variants));
  
      navigate(`/profile?plan=${planId}${!isEmptyObject(userPlanDetails) ? "" : "&ftime=1"}`);
    } catch (error) {
      console.error("Error submitting user plan:", error);
    }
  };

  const getUserPlanDetails = async (subId) => {
    try {
      const response = await doRequest(
        { order_id: subId },
        "POST",
        "order/get-subscription-details"
      );
      if (response?.resultFlag === 1 && response?.response?.has_scheduled_changes) {
        // handle scheduled changes if needed
      }
    } catch (error) {
      console.error("Error fetching user plan details:", error);
    }
  };

  /** ───────────────────────────────
   * Handlers
   ─────────────────────────────── */
  const handleClick = async () => {
    if (!userData) return navigate(`/Login?plan=${planId}`);
    if (planType === "Free") return submitUserPlan();
    navigate(`/payment?plan=${planId}`);
  };

  const handleCancelClick = () => setShowCancelPopup(true);

  const handleConfirmCancel = async () => {
    if (!userData?.id) return alert("Please login again");

    try {
      setShowCancelPopup(false);
      const payload = {
        id: userCurrentPlan.id,
        cancel_at_cycle_end: true,
        user_id: userData.id,
      };

      const response = await doRequest(payload, "POST", "order/subs-cancel");
      if (+response?.resultFlag === 1) {
        dispatch(getPlanDetails());
      }
    } catch (error) {
      console.error("Error cancelling plan:", error);
    }
  };

  /** ───────────────────────────────
   * Effects
   ─────────────────────────────── */
  useEffect(() => {
    if (userData?.subs_id) {
      getUserPlanDetails(userData.subs_id);
    }
  }, [userData?.subs_id]);

  /** ───────────────────────────────
   * Conditional Renders
   ─────────────────────────────── */
  // Case 1: User has no current plan
  if (!userCurrentPlan?.plan_id) {
    return (
      <button onClick={handleClick} className={btnClass}>
        {buttonText}
      </button>
    );
  }

  // Case 2: User has Free plan (1) and viewing Prepaid plan (5)
  if (currentPlanId === 1 && planId === 5) {
    return (
      <button onClick={handleClick} className={btnClass}>
        Get Prepaid
      </button>
    );
  }

  const isActivePlan = currentPlanStatus === "active" && !planExpired;

  /** ───────────────────────────────
   * JSX Render
   ─────────────────────────────── */
  return (
    <>
      {showCancelPopup && (
        <ConfirmationPopup
          close={() => setShowCancelPopup(false)}
          confirm={handleConfirmCancel}
          heading="Are you sure you want to cancel this subscription?"
        />
      )}

      {/* Case 3: Different Plan (Upgrade/Downgrade) */}
      {!isCurrentPlan && (
        <>
          {+planId !== 5 && (
            <button
              onClick={handleClick}
              // disabled={isCurrentPlan || planExpired || notEligible}
              disabled={notEligible || (isCurrentPlan && currentPlanStatus === "active")}
              className={btnClass}
            >
              {buttonText}
            </button>
          )}

          {currentPlanStatus !== "active" &&
            currentPlanId > 5 &&
            planId === 5 && (
              <button onClick={handleClick} className={btnClass}>
                Get Prepaid
              </button>
            )}
        </>
      )}

      {/* Case 4: Current Plan */}

      {isCurrentPlan && currentPlanId >= 5 && (
      <>
        {currentPlanId > 5 ? (
          isActivePlan ? (
            <button onClick={handleCancelClick} className={btnClass}>
              Cancel Plan
            </button>
          ) : (
            <>
              <button disabled>Subscription Expired</button>
              <button onClick={handleClick} className={`${btnClass} ms-3`}>
                {buttonText}
              </button>
            </>
          )
        ) : (
          <button onClick={handleClick} className={btnClass}>
            Get Prepaid
          </button>
        )}
      </>
    )}

    </>
  );
}
